import { Box, Stack, Typography } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { setFilterKeyword } from "src/context/reducers/filter-settings/filterSlice";
import { LightTooltip } from "../../tooltips/light";
import useLocales from "src/locales/use-locales";

export function renderHeader(params: any) {
    const { t } = useLocales();
    return (
        <LightTooltip title={t('dashboard.nonBrand.tooltips.clicks')}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                    {t("dashboard.nb_brand_grid.columns.title.search_term")}
                </Typography>
                <Box flexGrow={1} />

                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    style={{ minWidth: '16px', height: '16px' }}
                    fill="none"
                >
                    <g clipPath="url(#clip0_1128_7170)">
                        <path
                            d="M5.99992 2.33301V1.33301M3.37369 3.37345L2.66659 2.66634M3.37369 8.66634L2.66659 9.37345M8.66659 3.37345L9.37369 2.66634M2.33325 5.99967H1.33325M10.5762 10.7927L8.91508 13.8777C8.7253 14.2301 8.63042 14.4063 8.51625 14.4509C8.41718 14.4896 8.3056 14.4787 8.2159 14.4215C8.11255 14.3557 8.05363 14.1644 7.93579 13.7818L5.63004 6.29651C5.53378 5.98401 5.48565 5.82776 5.52438 5.72223C5.55811 5.63031 5.63055 5.55787 5.72247 5.52414C5.82801 5.48541 5.98425 5.53354 6.29675 5.6298L13.7821 7.93557C14.1646 8.05341 14.3559 8.11233 14.4217 8.21569C14.4789 8.30538 14.4898 8.41696 14.4511 8.51604C14.4066 8.6302 14.2303 8.72509 13.8779 8.91486L10.793 10.576C10.7406 10.6042 10.7144 10.6183 10.6915 10.6364C10.6711 10.6525 10.6527 10.6709 10.6367 10.6912C10.6185 10.7142 10.6044 10.7404 10.5762 10.7927Z"
                            stroke="#5E6D7B"
                            strokeWidth="1.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </g>
                    <defs>
                        <clipPath id="clip0_1128_7170">
                            <rect width="16" height="16" fill="white" />
                        </clipPath>
                    </defs>
                </svg>
            </Stack>
        </LightTooltip>
    );
}

/** Search term only (no clicks); a click filters the whole page on it, "Apply Filter" shows on hover. */
function SearchTermCell({ searchTerm }: { searchTerm: string }) {
    const { t } = useLocales();
    const dispatch = useDispatch();
    const label = t('dashboard.nonBrand.columns.applyFilter');
    const applyFilter = () => dispatch(setFilterKeyword([searchTerm]));
    return (
        <Box
            role="button"
            tabIndex={0}
            aria-label={`${label}: ${searchTerm}`}
            onClick={applyFilter}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') applyFilter();
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                // Shown on hover only, without reserving its room before: the term keeps the whole cell
                '&:hover .apply-filter, &:focus-visible .apply-filter': { display: 'inline' },
            }}
        >
            <Typography variant="body2" noWrap sx={{ fontWeight: 400, minWidth: 0 }}>
                {searchTerm}
            </Typography>
            <Typography
                component="span"
                className="apply-filter"
                sx={{ ml: 'auto', flexShrink: 0, display: 'none', fontSize: 12, fontWeight: 500, color: '#1E88E5' }}
            >
                {label}
            </Typography>
        </Box>
    );
}

export const renderCell = (params: GridRenderCellParams) => <SearchTermCell searchTerm={params.row.search_term} />;

export default {
    field: 'search_term',
    position: 0,
    headerName: 'dashboard.nb_brand_grid.columns.title.search_term',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.search_term',
    minWidth: 130,
    flex: 1.85,
    renderHeader: renderHeader,
    renderCell: renderCell,
}